import { Command } from 'commander';
import { RedmineConfigService } from '../../services/redmine-config.service.js';
import { syncIssue, extractIssueIdFromUrl } from '../../sync-issue.js';
import { ValidationError } from '../../errors/index.js';

interface SyncIssueOptions {
    id?: string;
    url?: string;
    dryRun?: boolean;
    config?: string;
    outputDir?: string;
    json?: boolean;
}

/**
 * Create redmine sync issue command
 */
export function createSyncIssueCommand(): Command {
    const cmd = new Command('issue')
        .description('Sync a single issue')
        .option('-i, --id <number>', 'Issue ID')
        .option('-u, --url <url>', 'Issue URL')
        .option('--dry-run', 'Preview without making changes')
        .option('-c, --config <path>', 'Config file path')
        .option('-o, --output-dir <path>', 'Output directory')
        .option('--json', 'Output as JSON')
        .action(async (options: SyncIssueOptions) => {
            await handleSyncIssue(options);
        });

    return cmd;
}

async function handleSyncIssue(options: SyncIssueOptions): Promise<void> {
    const configService = new RedmineConfigService(options.config);
    const config = await configService.load();

    let issueId: number;

    if (options.id) {
        issueId = parseInt(options.id, 10);
        if (isNaN(issueId)) {
            throw new ValidationError('Invalid issue ID');
        }
    } else if (options.url) {
        const extractedId = extractIssueIdFromUrl(options.url);
        if (!extractedId) {
            throw new ValidationError('Could not extract issue ID from URL');
        }
        issueId = extractedId;
    } else {
        throw new ValidationError('Either --id or --url must be provided');
    }

    const result = await syncIssue(issueId, config, {
        dryRun: options.dryRun,
        outputDir: options.outputDir,
    });

    if (options.json) {
        console.log(JSON.stringify(result, null, 2));
    } else {
        if (result.success) {
            const icon = result.action === 'skipped' ? '⏭️' : '✅';
            console.log(`${icon} ${result.message}`);
            if (result.filePath) {
                console.log(`   File: ${result.filePath}`);
            }
        } else {
            console.error('❌', result.message);
            process.exit(4);
        }
    }
}
